/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/dvrdDatePicker.scss';
import {AwesomeIcon, ChangeFunction, ErrorType, WithBackground} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {BolmateButton} from '../index';
import {useTranslation} from 'react-i18next';
import {padNum} from '../../../util/utils.ts';

interface Props {
    onChange: ChangeFunction<IDate>;
    closeOnChange?: boolean;
    value: IDate | null;
    label: string;
    error?: ErrorType;
    className?: string;
    placeholder?: string;
    dateFormat?: string;
    disabled?: boolean;
    min?: IDate | null;
    max?: IDate | null;
    allowBodyScroll?: boolean;
}

export default function DvrdDatePicker(props: Props) {
    const {
        onChange,
        className,
        closeOnChange,
        error,
        label,
        value,
        dateFormat,
        placeholder,
        disabled,
        max,
        min,
        allowBodyScroll
    } = props;
    const [pickerOpen, setPickerOpen] = useState(false);

    function onClickContainer() {
        if (!disabled) setPickerOpen(true);
    }

    const onChangePicker = (daySelected: boolean) => (value: IDate) => {
        if (min && value.isBefore(min, 'day')) return;
        if (max && value.isAfter(max, 'day')) return;
        onChange(value.clone());
        if (closeOnChange && daySelected) onClosePicker();
    };

    function onClosePicker() {
        setPickerOpen(false);
    }

    const format = dateFormat ?? 'YYYY-MM-DD';

    return (
        <div className={classNames('dvrd-date-picker', className, error && 'error', disabled && 'disabled')}>
            <label className='field-label'>{label}</label>
            <div className='value-container' onClick={onClickContainer}>
                <label className={classNames('value', !value && 'placeholder')}>
                    {value?.format(format) ?? placeholder ?? ''}
                </label>
                <AwesomeIcon name='calendar-alt' className='calendar-icon' />
            </div>
            <Picker
                onClose={onClosePicker}
                onChange={onChangePicker}
                open={pickerOpen}
                value={value}
                max={max}
                min={min}
                allowBodyScroll={allowBodyScroll}
            />
            <label className='error-label'>{error}</label>
        </div>
    );
}

interface PickerProps {
    onChange: (daySelected: boolean) => ChangeFunction<IDate>;
    onClose: VoidFunction;
    value: IDate | null;
    open: boolean;
    min?: IDate | null;
    max?: IDate | null;
    allowBodyScroll?: boolean;
}

type Day = {
    number: number;
    value: IDate;
    currentMonth: boolean;
};

const beforePadDays = [6, 0, 1, 2, 3, 4, 5];

function buildFirstDaysRow(firstDay: IDate): [Array<Day> | null, IDate] {
    const padBeforeDays = beforePadDays[firstDay.weekday()];
    if (!padBeforeDays) return [null, firstDay];
    const firstRow: Array<Day> = [];
    for (let offset = padBeforeDays; offset >= 1; offset -= 1) {
        const dayValue = firstDay.subtract(offset, 'days');
        firstRow.push({
            number: dayValue.day(),
            value: dayValue,
            currentMonth: false
        });
    }
    let secondRowFirstDay = new IDate(firstDay);
    while (firstRow.length < 7) {
        firstRow.push({
            number: secondRowFirstDay.day(),
            value: secondRowFirstDay,
            currentMonth: true
        });
        secondRowFirstDay = secondRowFirstDay.add(1, 'day');
    }
    return [firstRow, secondRowFirstDay];
}

function buildDay(value: IDate, year: number, month: number): Day {
    return {
        number: value.day(),
        value,
        currentMonth: value.year() === year && value.month() === month
    };
}

const daysToRender = 42; // Always render 6 rows of days

function Picker(props: PickerProps) {
    const {onChange, open, onClose, min, max, allowBodyScroll} = props;
    const pickerValue = new IDate(props.value ?? props.max);
    const days = useMemo(() => {
        const year = pickerValue.year();
        const month = pickerValue.month();
        const days: Array<Day> = [];
        let firstDay = new IDate(`${year}-${padNum(month)}-01`);
        const [firstRow, nextFirstDay] = buildFirstDaysRow(firstDay);
        if (firstRow) days.push(...firstRow);
        firstDay = nextFirstDay;
        while (days.length < daysToRender) {
            for (let ix = 0; ix < 7; ix++) {
                days.push(buildDay(firstDay, year, month));
                firstDay = firstDay.add(1, 'day');
            }
        }
        return days;
    }, [pickerValue]);
    const {t} = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    function onReduce(key: 'year' | 'month') {
        return function () {
            const currentDay = pickerValue.day();
            let newValue = pickerValue.day(1).subtract(1, key);
            newValue = newValue.day(Math.min(newValue.daysInMonth(), currentDay));
            onChange(false)(newValue);
        };
    }

    function onAdd(key: 'year' | 'month') {
        return function () {
            const currentDay = pickerValue.day();
            let newValue = pickerValue.day(1).add(1, key);
            newValue = newValue.day(Math.min(newValue.daysInMonth(), currentDay));
            onChange(false)(newValue);
        };
    }

    function onResetSwitcher(key: 'year' | 'month') {
        return function () {
            const now = IDate.now();
            const currentDay = pickerValue.day();
            let newValue: IDate;
            if (key === 'year') newValue = pickerValue.year(now.year());
            else newValue = pickerValue.month(now.month());
            newValue = newValue.day(Math.min(newValue.daysInMonth(), currentDay));
            onChange(false)(newValue);
        };
    }

    function onClickToday() {
        onChange(true)(IDate.now());
    }

    function onSelectDay(day: Day) {
        return function () {
            onChange(true)(day.value);
        };
    }

    function keyListener(evt: KeyboardEvent) {
        const {key, shiftKey, ctrlKey} = evt;
        if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(key)) {
            evt.preventDefault();
        }
        if (key === 'ArrowLeft') {
            onLeftKey(shiftKey, ctrlKey);
        } else if (key === 'ArrowRight') {
            onRightKey(shiftKey, ctrlKey);
        } else if (key === 'ArrowUp') {
            onUpKey();
        } else if (key === 'ArrowDown') {
            onDownKey();
        } else if (key === 'Enter') {
            onClose();
        }
    }

    function onLeftKey(shiftKey: boolean, ctrlKey: boolean) {
        if (shiftKey) {
            if (ctrlKey) onReduce('year')();
            else onReduce('month')();
        } else onChange(false)(pickerValue.add(-1, 'd'));
    }

    function onRightKey(shiftKey: boolean, ctrlKey: boolean) {
        if (shiftKey) {
            if (ctrlKey) onAdd('year')();
            else onAdd('month')();
        } else onChange(false)(pickerValue.add(1, 'd'));
    }

    function onUpKey() {
        onChange(false)(pickerValue.add(-7, 'd'));
    }

    function onDownKey() {
        onChange(false)(pickerValue.add(7, 'd'));
    }

    function addMountClass() {
        if (!ref.current?.classList.contains('switch-mount')) ref.current?.classList.add('switch-mount');
    }

    function removeMountClass() {
        ref.current?.classList.remove('switch-mount');
    }

    function renderSwitcher(key: 'year' | 'month', kind: 'add' | 'reduce') {
        const icon = kind === 'add' ? 'chevron-right' : 'chevron-left',
            title = kind === 'add' ? 'Volgende' : 'Vorige',
            onClick = kind === 'add' ? onAdd(key) : onReduce(key);
        return (
            <AwesomeIcon name={icon} title={title} className={classNames('switcher-icon', kind)} onClick={onClick} />
        );
    }

    function renderTooltipContainer() {
        return (
            <div className='tooltip-container'>
                <AwesomeIcon name='question-circle' className='tooltip-icon' prefix='far' />
                <div className='tooltip'>
                    <label className='tooltip-label'>Shortcuts</label>
                    <div className='key'>
                        <AwesomeIcon name='arrow-left' className='shortcut' />
                        <span className='directive'>-1 dag</span>
                    </div>
                    <div className='key'>
                        <div className='directives'>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-left' className='shortcut' />
                        </div>
                        <span className='directive'>-1 maand</span>
                    </div>
                    <div className='key'>
                        <div className='directives full'>
                            <span className='shortcut text'>CTRL</span>
                            <span className='directive'>+</span>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-left' className='shortcut' />
                        </div>
                        <span className='directive'>-1 jaar</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-right' className='shortcut' />
                        <span className='directive'>+1 dag</span>
                    </div>
                    <div className='key'>
                        <div className='directives'>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-right' className='shortcut' />
                        </div>
                        <span className='directive'>+1 maand</span>
                    </div>
                    <div className='key'>
                        <div className='directives full'>
                            <span className='shortcut text'>CTRL</span>
                            <span className='directive'>+</span>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-right' className='shortcut' />
                        </div>
                        <span className='directive'>+1 jaar</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-up' className='shortcut' />
                        <span className='directive'>-7 dagen</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-down' className='shortcut' />
                        <span className='directive'>+7 dagen</span>
                    </div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', keyListener);
            addMountClass();
            window.setTimeout(removeMountClass, 500);
        } else document.removeEventListener('keydown', keyListener);

        return function () {
            document.removeEventListener('keydown', keyListener);
        };
    }, [open]);

    function renderDay(day: Day, index: number) {
        const disabled = (min && day.value.isBefore(min)) || (max && day.value.isAfter(max));
        const selected = pickerValue?.isAt(day.value, 'day');
        const onClick = disabled ? undefined : onSelectDay(day);
        return (
            <div
                className={classNames('day', !day.currentMonth && 'pad', day.number === 0 && 'hide', {
                    disabled,
                    selected
                })}
                key={index}
                onClick={onClick}
            >
                <span className='day-label'>{day.number}</span>
            </div>
        );
    }

    return (
        <WithBackground active={open} onClose={onClose} allowBodyScroll={allowBodyScroll}>
            <div className='picker' ref={ref}>
                <div className='switcher year'>
                    {renderSwitcher('year', 'reduce')}
                    <label className='switcher-label' onClick={onResetSwitcher('year')}>
                        {pickerValue.year()}
                    </label>
                    {renderSwitcher('year', 'add')}
                </div>
                <div className='switcher month'>
                    {renderSwitcher('month', 'reduce')}
                    <label className='switcher-label' onClick={onResetSwitcher('month')}>
                        {pickerValue.format('MMMM')}
                    </label>
                    {renderSwitcher('month', 'add')}
                </div>
                <div className='current-date-container'>
                    <label className='current-date-label'>{pickerValue.format('dddd D MMMM YYYY')}</label>
                    {renderTooltipContainer()}
                </div>
                <div className='days-container'>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.mon', 'Ma')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.tue', 'Di')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.wed', 'Wo')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.thu', 'Do')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.fri', 'Vr')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.sat', 'Za')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.sun', 'Zo')}</span>
                    </div>
                    {days.map(renderDay)}
                </div>
                <div className='actions-container'>
                    <BolmateButton
                        onClick={onClickToday}
                        label={t('controls.datePicker.today', 'Vandaag')}
                        className='action'
                    />
                    <BolmateButton
                        onClick={onClose}
                        label={t('controls.datePicker.close', 'Sluiten')}
                        secondary
                        className='action'
                    />
                </div>
            </div>
        </WithBackground>
    );
}
